import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "pending_payment", cls: "bg-amber-500/15 border-amber-500/40 text-amber-400", icon: "⧗" },
  paid: { label: "paid", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400", icon: "✓" },
  cancelled: { label: "payment_cancelled", cls: "bg-neutral-500/15 border-neutral-500/40 text-neutral-400", icon: "✕" },
  failed: { label: "payment_failed", cls: "bg-[#e05555]/15 border-[#e05555]/40 text-[#e05555]", icon: "!" },
  refunded: { label: "payment_cancelled", cls: "bg-sky-500/15 border-sky-500/40 text-sky-400", icon: "↩" },
};

export default function AdminPaymentsPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [players, setPlayers] = useState([]);

  const load = async () => {
    const r = await axios.get(`${API}/admin/bookings`, {
      headers,
      params: status ? { status } : {},
    });
    setBookings(r.data);
  };
  useEffect(() => {
    if (!password) { navigate("/admin"); return; }
    load();
    axios.get(`${API}/admin/players`, { headers }).then((r) => setPlayers(r.data));
    // eslint-disable-next-line
  }, [status]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return bookings;
    return bookings.filter((b) =>
      `${b.player_number} ${b.player_name} ${b.service_title}`.toLowerCase().includes(s)
    );
  }, [bookings, search]);

  const thisMonthTotal = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return bookings
      .filter((b) => b.payment_status === "paid" && (b.payment_date || "") >= start)
      .reduce((sum, b) => sum + (b.amount_cents || 0), 0);
  }, [bookings]);

  const patch = async (b, updates) => {
    const r = await axios.patch(`${API}/admin/bookings/${b.id}`, updates, { headers });
    setBookings((arr) => arr.map((x) => (x.id === b.id ? r.data : x)));
  };

  const exportCSV = () => {
    const headers2 = ["#", "Player", "Service", "Amount USD", "Status", "Confirmed", "Booking Date", "Payment Date"];
    const rows = filtered.map((b) => [
      b.player_number, b.player_name, b.service_title,
      (b.amount_cents / 100).toFixed(2), b.payment_status,
      b.spot_confirmed ? "Yes" : "No",
      b.booking_date, b.payment_date || "",
    ]);
    const csv = [headers2, ...rows].map((r) => r.map((c) => {
      const v = (c ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(v) ? `"${v}"` : v;
    }).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `soccer-troop-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
          <div>
            <h1 className="font-bebas text-4xl sm:text-5xl tracking-[0.1em]">💳 {t("payments")}</h1>
            <p className="text-sm text-neutral-400 mt-1">
              {t("total_month")}: <span className="text-[#e8559a] font-bebas text-xl tracking-widest">${(thisMonthTotal / 100).toFixed(2)}</span>
              {" · "}{t("total_bookings")}: <span className="text-white font-bebas tracking-widest">{filtered.length}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setManualOpen(true)}
              data-testid="new-manual-booking-btn"
              className="px-4 py-2 rounded-lg bg-[#161616] border border-[#e8559a]/30 hover:border-[#e8559a] text-[#e8559a] text-sm transition-colors"
            >
              + {t("new_manual_booking")}
            </button>
            <button
              onClick={exportCSV}
              className="px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white text-sm transition-colors"
            >
              ⬇ CSV
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-[#161616] border border-[#252525] p-4 mb-5 flex flex-col sm:flex-row gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar jugador, servicio..."
            data-testid="payments-search"
            className="flex-1 bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-2.5 text-white placeholder:text-neutral-600 focus:border-[#e8559a] focus:outline-none"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="payments-filter"
            className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-2.5 text-white focus:border-[#e8559a] focus:outline-none"
          >
            <option value="">{t("filter_all")}</option>
            <option value="pending">{t("pending_payment")}</option>
            <option value="paid">{t("paid")}</option>
            <option value="cancelled">{t("payment_cancelled")}</option>
            <option value="failed">{t("payment_failed")}</option>
          </select>
        </div>

        <div className="rounded-xl bg-[#161616] border border-[#252525] overflow-hidden" data-testid="payments-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <tr>
                  {["#", t("table_name"), t("service"), t("amount"), t("payment_status"), t("payment_date"), t("booking_date"), t("table_actions")].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-neutral-500">{t("no_bookings")}</td></tr>
                ) : (
                  filtered.map((b) => {
                    const meta = STATUS_META[b.payment_status] || STATUS_META.pending;
                    return (
                      <tr key={b.id} className="border-t border-[#252525] hover:bg-[#1c1c1c] transition-colors" data-testid={`booking-row-${b.id}`}>
                        <td className="px-4 py-3 font-bebas text-lg text-[#e8559a] tracking-widest">{b.player_number}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{b.player_name}</td>
                        <td className="px-4 py-3 max-w-xs truncate">{b.service_title}</td>
                        <td className="px-4 py-3 font-bebas text-lg tracking-widest">${(b.amount_cents / 100).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full border text-xs ${meta.cls}`}>
                            {meta.icon} {t(meta.label)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-neutral-400 text-xs whitespace-nowrap">
                          {b.payment_date ? new Date(b.payment_date).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-neutral-400 text-xs whitespace-nowrap">
                          {new Date(b.booking_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5">
                            {b.payment_status === "pending" && (
                              <button
                                onClick={() => patch(b, { payment_status: "paid" })}
                                data-testid={`mark-paid-${b.id}`}
                                className="px-2.5 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs hover:bg-emerald-500/20 transition-colors"
                                title="Mark as Paid (manual)"
                              >
                                ✓
                              </button>
                            )}
                            {b.payment_status !== "cancelled" && b.payment_status !== "paid" && (
                              <button
                                onClick={() => patch(b, { payment_status: "cancelled" })}
                                data-testid={`cancel-${b.id}`}
                                className="px-2.5 py-1.5 rounded-md bg-[#e05555]/10 border border-[#e05555]/30 text-[#e05555] text-xs hover:bg-[#e05555]/20 transition-colors"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {manualOpen && (
        <ManualBookingModal
          players={players}
          onClose={() => setManualOpen(false)}
          onCreated={(b) => { setBookings((arr) => [b, ...arr]); setManualOpen(false); }}
          t={t}
        />
      )}
    </div>
  );
}

function ManualBookingModal({ players, onClose, onCreated, t }) {
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };
  const [playerId, setPlayerId] = useState("");
  const [service, setService] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("paid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!playerId || !service || !amount) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/bookings`, {
        player_id: playerId,
        service_type: "custom",
        service_title: service,
        amount_cents: Math.round(parseFloat(amount) * 100),
        payment_status: status,
        payment_provider: status === "paid" ? "manual_cash" : null,
      }, { headers });
      onCreated(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#e8559a] focus:outline-none";

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#161616] border border-[#252525] rounded-2xl p-6" data-testid="manual-booking-modal">
        <h2 className="font-bebas text-2xl tracking-widest mb-4 pb-3 border-b border-[#252525]">
          + {t("new_manual_booking")}
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">Jugador</label>
            <select value={playerId} onChange={(e) => setPlayerId(e.target.value)} className={inputCls} required>
              <option value="">{t("select")}</option>
              {players.map((p) => (
                <option key={p.id} value={p.id}>#{p.player_number} · {p.first_name} {p.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("service")}</label>
            <input value={service} onChange={(e) => setService(e.target.value)} placeholder="Camp Verano · Semana 1" className={inputCls} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("amount")}</label>
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="70.00" className={inputCls} required />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("payment_status")}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="paid">{t("paid")} (efectivo)</option>
                <option value="pending">{t("pending_payment")}</option>
              </select>
            </div>
          </div>
        </div>
        {error && <div className="mt-3 p-2.5 rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm">{error}</div>}
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 text-sm transition-colors">{t("cancel")}</button>
          <button type="submit" disabled={busy} data-testid="manual-booking-save" className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest transition-colors">
            {busy ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}
